import React from 'react';
import { Card } from '../components/ui/card';
import { Users, Target, Heart, Award } from 'lucide-react';

export const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Confianza',
      description: 'Construimos relaciones duraderas basadas en la transparencia y honestidad.'
    },
    {
      icon: Users,
      title: 'Compromiso',
      description: 'Estamos comprometidos con el bienestar financiero de nuestros clientes.'
    },
    {
      icon: Target,
      title: 'Accesibilidad',
      description: 'Hacemos que el crédito sea accesible para todos, sin complicaciones.'
    },
    {
      icon: Award,
      title: 'Excelencia',
      description: 'Brindamos un servicio de calidad con atención personalizada.'
    }
  ];

  return (
    <div className="min-h-screen pt-32 pb-20 px-4">
      {/* Hero Section */}
      <section className="container mx-auto mb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 animate-in fade-in slide-in-from-left duration-700">
            <h1 className="text-5xl md:text-6xl font-bold leading-tight">
              <span className="text-slate-900">Conoce a</span>
              <br />
              <span className="bg-gradient-to-r from-amber-500 to-emerald-500 bg-clip-text text-transparent">
                PrestaNet
              </span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Somos una microfinanciera comprometida con hacer que el acceso al crédito sea simple, rápido y transparente para las personas que más lo necesitan.
            </p>
          </div>
          <div className="relative animate-in fade-in slide-in-from-right duration-700">
            <img 
              src="https://customer-assets.emergentagent.com/job_f35f5b38-6d8d-497e-9765-fd0e7def8902/artifacts/hh17c75d_ChatGPT%20Image%2015%20ene%202026%2C%2009_16_06%20p.m..png" 
              alt="Net el robot" 
              className="w-full max-w-md mx-auto drop-shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="container mx-auto mb-20">
        <Card className="p-8 md:p-12 bg-gradient-to-br from-amber-50 to-emerald-50 border-2 border-amber-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center">
              Nuestra Misión
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed text-center mb-8">
              En PrestaNet, creemos que todos merecen acceso a servicios financieros justos y transparentes. 
              Nuestro modelo persona a persona elimina las barreras tradicionales y crea un sistema de 
              préstamos más humano y accesible.
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="space-y-2">
                <div className="text-4xl font-bold bg-gradient-to-r from-amber-500 to-emerald-500 bg-clip-text text-transparent">
                  2020
                </div>
                <div className="text-slate-600">Año de Fundación</div>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-bold bg-gradient-to-r from-amber-500 to-emerald-500 bg-clip-text text-transparent">
                  6
                </div>
                <div className="text-slate-600">Estados con Presencia</div>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-bold bg-gradient-to-r from-amber-500 to-emerald-500 bg-clip-text text-transparent">
                  5K+
                </div>
                <div className="text-slate-600">Créditos Otorgados</div>
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* Values Section */}
      <section className="container mx-auto mb-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Nuestros Valores
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Los principios que guían cada una de nuestras decisiones
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <Card 
              key={index} 
              className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-amber-200 group"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-emerald-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <value.icon className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{value.title}</h3>
              <p className="text-slate-600">{value.description}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Team Section with Net */}
      <section className="container mx-auto">
        <Card className="p-8 md:p-12 bg-slate-900 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500 rounded-full blur-3xl opacity-20"></div>
          <div className="relative z-10 grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Conoce a Net, nuestro asistente
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                Net es más que nuestra mascota, es el símbolo de nuestra innovación y compromiso con la tecnología 
                para brindarte el mejor servicio. Net representa la rapidez, eficiencia y amabilidad que nos caracteriza.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed">
                Con Net, hacer un préstamo es tan fácil como hablar con un amigo. Estamos disponibles 
                para ayudarte en cada paso del camino.
              </p>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_f35f5b38-6d8d-497e-9765-fd0e7def8902/artifacts/hh17c75d_ChatGPT%20Image%2015%20ene%202026%2C%2009_16_06%20p.m..png" 
                alt="Net el robot" 
                className="w-full max-w-sm drop-shadow-2xl animate-bounce-slow"
              />
            </div>
          </div>
        </Card>
      </section>

      {/* Coverage Section */}
      <section className="container mx-auto mt-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Nuestra Cobertura
          </h2>
          <p className="text-xl text-slate-600">
            Presentes en 6 estados de la República Mexicana
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {['Guadalajara', 'Michoacán', 'Guerrero', 'Colima', 'San Luis Potosí', 'Tepic'].map((state, index) => (
            <Card 
              key={index} 
              className="p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1 bg-gradient-to-br from-amber-50 to-emerald-50 border-2 border-amber-100"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-emerald-400 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-bold text-lg">
                {index + 1}
              </div>
              <h3 className="text-xl font-bold text-slate-900">{state}</h3>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
};

export default About;
